import { RestResource } from "./common"
import { SchemaDeSerBuilder, type SchemaDeSer } from "./deser"
import type { JsonHelper } from "./util/json"
import { isNilOrEmpty } from "./util/string"

const LONG_NET_CHANGE_ROOT_SYMBOLS = new Set(["/6J", "/MJY"])
const TWO_DIGIT_PAD = 2
const TWO_DIGIT_YEAR_SLICE = -2

const EXCHANGE_CODE = {
  UNKNOWN: "UNKNOWN",
  XCME: "XCME",
  XCBT: "XCBT",
  XCEC: "XCEC",
  XNYM: "XNYM",
  XCBF: "XCBF",
  SMFE: "SMFE",
  CXERX: "CXERX",
} as const

export type ExchangeCode = (typeof EXCHANGE_CODE)[keyof typeof EXCHANGE_CODE]

function parseExchangeCode(value: string | null | undefined): ExchangeCode {
  if (isNilOrEmpty(value)) {
    return EXCHANGE_CODE.UNKNOWN
  }

  const match = (Object.values(EXCHANGE_CODE) as ExchangeCode[]).find(
    (code) => code === value
  )
  return match ?? EXCHANGE_CODE.UNKNOWN
}

export const ExchangeCode = {
  ...EXCHANGE_CODE,
  parse: parseExchangeCode,
}

export class ProductSubtype {
  static readonly NONE = new ProductSubtype("")
  static readonly CRYPTO = new ProductSubtype("Cryptocurrency")
  static readonly EVENT = new ProductSubtype("Event")

  private constructor(readonly serialized: string) {}

  isEventContract(): boolean {
    return this === ProductSubtype.EVENT
  }

  isCryptocurrencyContract(): boolean {
    return this === ProductSubtype.CRYPTO
  }

  static parse(raw: string | null | undefined): ProductSubtype {
    if (raw === null || raw === undefined || raw.trim() === "") {
      return ProductSubtype.NONE
    }

    const trimmed = raw.trim()
    if (trimmed === ProductSubtype.CRYPTO.serialized) {
      return ProductSubtype.CRYPTO
    }
    if (trimmed === ProductSubtype.EVENT.serialized) {
      return ProductSubtype.EVENT
    }

    return ProductSubtype.NONE
  }
}

export class EtfEquivalent {
  symbol = ""
  shareQuantity = 0
}

export const ETF_EQUIVALENT_DESER: SchemaDeSer<EtfEquivalent> =
  new SchemaDeSerBuilder(EtfEquivalent)
    .ofString("symbol")
    .ofFloat("shareQuantity")
    .toDeSer()

export interface PriceStepConfig {
  step: number
}

export interface PriceFormatter {
  step: number
}

export class FuturesContract extends RestResource {
  static readonly DEFAULT_NOTIONAL_MULTIPLIER = 1

  symbol = ""
  productCode = ""
  contractSize = 0
  tickSize = 0
  tickSizeDecimal = ""
  notionalMultiplier = FuturesContract.DEFAULT_NOTIONAL_MULTIPLIER
  mainFraction = 0
  subFraction = 0
  displayFactor = 0
  firstNoticeDate: Date | null = null
  lastTradeDate: Date | null = null
  expirationDate: Date | null = null
  closingOnlyDate: Date | null = null
  activeMonth = false
  isClosingOnly = false
  etfEquivalent: EtfEquivalent | null = null
  streamerExchangeCode = ""
  rollTargets: string[] = []
  spreadPriceStepConfigs: Record<string, PriceStepConfig> = {}
  backMonthFirstCalendarSymbol = false
  exchangeCode: ExchangeCode = ExchangeCode.UNKNOWN
  underlyingPriceStepConfig: PriceStepConfig | null = null
  optionPriceStepConfig: PriceStepConfig | null = null
  underlyingPriceFormatter: PriceFormatter | null = null
  optionPriceFormatter: PriceFormatter | null = null
  productSubtype: ProductSubtype = ProductSubtype.NONE
  trueUnderlyingSymbol = ""

  isEventContract(): boolean {
    return this.productSubtype.isEventContract()
  }

  getTrueUnderlyingSymbol(): string {
    return this.trueUnderlyingSymbol
  }

  lookupSpreadPriceStepConfig(symbol: string): PriceStepConfig | null {
    return this.spreadPriceStepConfigs[symbol] ?? null
  }

  equals(other: unknown): boolean {
    if (this === other) {
      return true
    }

    if (!(other instanceof FuturesContract)) {
      return false
    }

    return this.symbol === other.symbol
  }

  static isLongNetChange(rootSymbol: string | null | undefined): boolean {
    if (rootSymbol === null || rootSymbol === undefined) {
      return false
    }

    return LONG_NET_CHANGE_ROOT_SYMBOLS.has(rootSymbol)
  }

  static toUnderlyingStepConfig(
    _subFraction: number,
    tickSize: string
  ): PriceStepConfig {
    return { step: Number.parseFloat(tickSize) }
  }

  static formatLastTradeDate = formatLastTradeDate
}

export const FUTURES_CONTRACT_DESER: SchemaDeSer<FuturesContract> =
  new SchemaDeSerBuilder(FuturesContract)
    .ofString("symbol")
    .ofString("productCode")
    .ofFloat("contractSize")
    .ofFloat("tickSize")
    .ofString("tickSizeDecimal", "tick-size")
    .ofFloat("notionalMultiplier")
    .ofFloat("mainFraction")
    .ofFloat("subFraction")
    .ofFloat("displayFactor")
    .ofDate("firstNoticeDate")
    .ofDate("lastTradeDate")
    .ofDate("expirationDate")
    .ofDate("closingOnlyDate")
    .ofBoolean("activeMonth")
    .ofBoolean("isClosingOnly")
    .ofNested(
      "etfEquivalent",
      ETF_EQUIVALENT_DESER,
      EtfEquivalent,
      "future-etf-equivalent"
    )
    .ofString("streamerExchangeCode")
    .ofBoolean("backMonthFirstCalendarSymbol")
    .toDeSer()

export function formatLastTradeDate(
  contract: FuturesContract | null | undefined
): string | null {
  if (contract === null || contract === undefined) {
    return null
  }

  const lastTradeDate = contract.lastTradeDate
  if (lastTradeDate === null) {
    return "Expired"
  }

  const month = lastTradeDate.getUTCMonth() + 1
  const day = String(lastTradeDate.getUTCDate()).padStart(TWO_DIGIT_PAD, "0")
  const year = String(lastTradeDate.getUTCFullYear()).slice(
    TWO_DIGIT_YEAR_SLICE
  )
  return `${month}/${day}/${year}`
}

function applyFutureProduct(
  contract: FuturesContract,
  helper: JsonHelper
): void {
  if (!helper.hasField("future-product")) {
    return
  }

  const futureProduct = helper.getChild("future-product")
  if (futureProduct.hasField("tick-size")) {
    contract.tickSizeDecimal = futureProduct.getString("tick-size")
    contract.tickSize = futureProduct.getFloat("tick-size")
  }
  if (futureProduct.hasField("notional-multiplier")) {
    contract.notionalMultiplier = futureProduct.getFloat("notional-multiplier")
  }
  if (
    !helper.hasField("streamer-exchange-code") &&
    futureProduct.hasField("streamer-exchange-code")
  ) {
    contract.streamerExchangeCode = futureProduct.getString(
      "streamer-exchange-code"
    )
  }
  contract.productSubtype = ProductSubtype.parse(
    futureProduct.getString("product-subtype")
  )
}

function parseSpreadPriceStepConfigs(
  helper: JsonHelper
): Record<string, PriceStepConfig> {
  const configs: Record<string, PriceStepConfig> = {}
  for (const child of helper.getChildren("spread-tick-sizes")) {
    const symbol = child.getString("symbol")
    if (symbol === "") {
      continue
    }

    configs[symbol] = { step: child.getFloat("value") }
  }
  return configs
}

function parseOptionPriceStepConfig(
  helper: JsonHelper,
  fallback: PriceStepConfig
): PriceStepConfig {
  const optionTicks = helper.getChildren("option-tick-sizes")
  if (optionTicks.length === 0) {
    return fallback
  }

  return { step: optionTicks[0].getFloat("value") }
}

export function parseFuturesContract(helper: JsonHelper): FuturesContract {
  const contract = FUTURES_CONTRACT_DESER.toParser(FuturesContract)(helper)

  applyFutureProduct(contract, helper)
  contract.exchangeCode = ExchangeCode.parse(contract.streamerExchangeCode)

  const rollTargetSymbol = helper.getString("roll-target-symbol")
  if (rollTargetSymbol !== "") {
    contract.rollTargets = [rollTargetSymbol]
  }

  contract.spreadPriceStepConfigs = parseSpreadPriceStepConfigs(helper)
  contract.underlyingPriceStepConfig = FuturesContract.toUnderlyingStepConfig(
    contract.subFraction,
    contract.tickSizeDecimal
  )
  contract.optionPriceStepConfig = parseOptionPriceStepConfig(
    helper,
    contract.underlyingPriceStepConfig
  )
  contract.underlyingPriceFormatter = { step: contract.tickSize }
  contract.optionPriceFormatter = {
    step: contract.optionPriceStepConfig.step,
  }

  if (contract.isEventContract()) {
    const trueUnderlyingSymbol = helper.getString("true-underlying-symbol")
    contract.trueUnderlyingSymbol =
      trueUnderlyingSymbol === "" ? contract.symbol : trueUnderlyingSymbol
  } else {
    contract.trueUnderlyingSymbol = contract.symbol
  }

  return contract
}
